import React, { useState, useCallback, useRef } from "react";
import { Upload, LoaderCircle, CircleCheckBig, FileWarning, AlertTriangle, RefreshCcw } from "lucide-react";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { toast } from "sonner";
import { generateKnowledgeBasePresignedUploadUrl, getFile } from "../../../../service/apiService";
import { useUser } from "../../../../context/UserContext";
import { addFileToKnowledgeBase } from "../../../../helpers/knowledgeBaseLocalStorage";

interface BrainBankFileUploaderProps {
  knowledgeBaseId: string;
  brainBankName: string;
  indexName: string;
  existingFiles?: string[];
  onFileUploaded?: () => void;
}

interface FileUploadStatus {
  file: File;
  progress: number;
  status: "uploading" | "uploaded" | "not_processed" | "processing" | "processed" | "failed" | "duplicate";
  errorMessage?: string;
  fileId?: string;
  uploadStarted?: boolean;
}

const BrainBankFileUploader: React.FC<BrainBankFileUploaderProps> = ({
  knowledgeBaseId,
  brainBankName,
  indexName,
  existingFiles = [],
  onFileUploaded
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<"uploading" | "uploaded" | "not_processed" | "processing" | "processed" | "failed" | "duplicate" | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { userId } = useUser();

  const duplicateFileNames = new Set<string>(
    existingFiles.map(filename => filename.toLowerCase())
  );

  const checkIfFileExists = (fileName: string): boolean => {
    if (!duplicateFileNames.size) return false;
    return duplicateFileNames.has(fileName.toLowerCase());
  };

  async function handleFileUpload(file: File) {
    if (!knowledgeBaseId || !indexName) {
      toast.error("No BrainBank selected");
      return;
    }

    const isDuplicate = checkIfFileExists(file.name);
    if (isDuplicate) {
      setUploadStatus("duplicate");
      setErrorMessage("This file already exists in the BrainBank. Please delete the existing file before uploading again.");
      setIsUploading(false);
      return;
    }

    setIsUploading(true);
    setUploadStatus("uploading");
    setUploadProgress(0);

    try {
      const presignedUrlResponse = await generateKnowledgeBasePresignedUploadUrl(
        file.name,
        indexName,
        knowledgeBaseId,
        "standard"
      );

      const { presigned_url, file_id } = presignedUrlResponse;

      await axios.put(presigned_url, file, {
        headers: {
          "Content-Type": file.type || "application/octet-stream",
          "x-amz-meta-created_by": userId,
          "x-amz-meta-updated_by": userId,
          "x-amz-meta-index_name": indexName,
          "x-amz-meta-file_id": file_id,
          "x-amz-meta-processing_mode": "standard"
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });

      setUploadStatus("uploaded");
      setUploadProgress(100);

      fetchFileStatus(file_id);

      // Notify parent component of successful upload
      if (onFileUploaded) {
        onFileUploaded();
      }

      // Reset file input for future uploads
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

    } catch (error) {
      console.error("Error uploading file:", error);
      setUploadStatus("failed");
      setErrorMessage("Failed to upload file. Please try again.");
      setIsUploading(false);
    }
  }

  const fetchFileStatus = async (fileId: string) => {
    try {
      const response = await getFile(fileId);
      addFileToKnowledgeBase(response);
      
      const fileStatus = response.status 
        ? response.status.toString() as FileUploadStatus['status'] 
        : "failed";
      
      setUploadStatus(fileStatus);
      setErrorMessage(fileStatus === "failed" ? "Processing failed" : null);
      
    } catch (error) {
      console.error("Error fetching file status:", error);
      setUploadStatus("failed");
      setErrorMessage("Failed to fetch file status.");
    } finally {
      setIsUploading(false);
    }
  };

  // Define the click handler for the "+ Add File into brainbank" button
  const handleAddFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle file selection
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileChange}
        accept=".pdf,.xlsx,.docx,.txt"
        disabled={isUploading}
      />
      
      {isUploading ? (
        <div className="flex items-center space-x-2">
          <div className="w-24 h-1 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-violet-600 transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <span className="text-xs text-gray-500">{uploadProgress}%</span>
        </div>
      ) : uploadStatus === "duplicate" ? (
        <div className="flex items-center space-x-2">
          <AlertTriangle className="h-3 w-3 text-amber-500" />
          <span className="text-violet-600 text-xs hover:text-violet-800 cursor-pointer" onClick={handleAddFileClick}>
            + Add File into brainbank
          </span>
          {errorMessage && (
            <span className="text-xs text-amber-600 ml-2">{errorMessage}</span>
          )}
        </div>
      ) : uploadStatus === "failed" ? (
        <div className="flex items-center space-x-2">
          <FileWarning className="h-3 w-3 text-red-500" />
          <span className="text-violet-600 text-xs hover:text-violet-800 cursor-pointer" onClick={handleAddFileClick}>
            + Add File into brainbank
          </span>
          {errorMessage && (
            <span className="text-xs text-red-500 ml-2">{errorMessage}</span>
          )}
        </div>
      ) : uploadStatus === "processing" ? (
        <div className="flex items-center space-x-2">
          <LoaderCircle className="h-3 w-3 text-yellow-500 animate-spin" />
          <span className="text-violet-600 text-xs hover:text-violet-800 cursor-pointer" onClick={handleAddFileClick}>
            + Add File into brainbank
          </span>
        </div>
      ) : uploadStatus === "processed" ? (
        <div className="flex items-center space-x-2">
          <CircleCheckBig className="h-3 w-3 text-green-600" />
          <span className="text-violet-600 text-xs hover:text-violet-800 cursor-pointer" onClick={handleAddFileClick}>
            + Add File into brainbank
          </span>
        </div>
      ) : (
        <span 
          className="text-violet-600 text-xs hover:text-violet-800 cursor-pointer flex items-center"
          onClick={handleAddFileClick}
        >
          <Upload className="h-3 w-3 mr-1" />
          + Add File into brainbank
        </span>
      )}
    </>
  );
};

export default BrainBankFileUploader;